import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const ProductFacts = () => {
  const { facts, v31, v32 } = useProduct();
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      const p = max > 0 ? el.scrollLeft / max : 0;
      setProgress(p);
      if (p > 0.05) setShowHint(false);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, []);

  if (!facts) return null;

  const scrollToAnchor = () => {
    const el = document.querySelector(`[data-testid="${facts.anchor}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="bg-riversol-paper" data-testid="product-facts">
      <div className="max-w-[1600px] mx-auto">
        <div className="relative border-b border-neutral-200">
          <div
            ref={trackRef}
            className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar scroll-smooth"
            style={{ scrollbarWidth: "none" }}
            data-testid="facts-slider"
          >
            {facts.rows.map((row, i) => (
              <div
                key={row.label}
                className="snap-start shrink-0 basis-[72%] sm:basis-[48%] md:basis-[32%] xl:basis-1/5 px-6 py-8 flex flex-col gap-3 border-l border-neutral-200 first:border-l-0"
              >
                <span className={`${v32 ? "font-sans" : "font-mono"} text-[13px] font-bold tracking-[0.12em] uppercase text-neutral-900 flex items-center gap-1.5`}>
                  {row.label}
                  {!v31 && <span className="text-riversol-blue/40 font-normal">/</span>}
                </span>
                {row.link ? (
                  <button
                    type="button"
                    onClick={scrollToAnchor}
                    data-testid="facts-ingredients-link"
                    className="group text-left text-[13px] leading-relaxed text-neutral-700 hover:text-riversol-blue transition-colors"
                  >
                    {row.value}
                    <span className="mt-2 flex items-center gap-1 font-mono text-[10px] tracking-[0.14em] uppercase text-riversol-blue">
                      See the actives
                      <ArrowDown size={12} className="group-hover:translate-y-0.5 transition-transform" />
                    </span>
                  </button>
                ) : (
                  <span className="text-[13px] leading-relaxed text-neutral-700">{row.value}</span>
                )}
              </div>
            ))}
          </div>

          {/* Right-edge fade hints there's more to see; hidden once the grid fits (xl) */}
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-riversol-paper to-transparent xl:hidden transition-opacity duration-300"
            style={{ opacity: progress > 0.9 ? 0 : 1 }}
          />

          {/* Animated swipe hint sits inside the track — disappears once user starts scrolling */}
          <div
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 right-4 xl:hidden transition-all duration-500 ${
              showHint ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
            }`}
            aria-hidden="true"
          >
            <div className="flex items-center gap-1.5 rounded-full bg-white/85 backdrop-blur px-3 py-1.5 shadow-[0_6px_16px_rgba(11,37,69,0.12)] border border-neutral-200">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue">Swipe</span>
              <ArrowRight size={12} className="text-riversol-blue animate-slidehint" />
            </div>
          </div>
        </div>

        {/* Progress bar — always visible on mobile/tablet to reinforce slider affordance */}
        <div className="px-8 py-4 flex items-center gap-3 xl:hidden" data-testid="facts-progress-bar">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400 shrink-0">
            {String(Math.min(facts.rows.length, Math.max(1, Math.round(progress * (facts.rows.length - 1)) + 1))).padStart(2, "0")} {v31 ? "·" : "/"} {String(facts.rows.length).padStart(2, "0")}
          </span>
          <div className="flex-1 h-[2px] bg-neutral-200 relative overflow-hidden rounded-full">
            <div
              className="absolute inset-y-0 left-0 bg-riversol-blue transition-[width] duration-150 ease-out"
              style={{ width: `${Math.max(15, progress * 100)}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductFacts;
